import type {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from 'next'
import { Layout } from '@components/common'
import Page from '@components/ui/Page'
import { useRouter } from 'next/router'
import { getGlobalContent, getPageContent, getAllPages } from '@lib/sanity.queries'
import TRANSLATIONS from '@locales/index'

// Paths that are assets or well-known, not CMS pages — avoid Sanity lookup and noisy logs
function isAssetOrNonPagePath(slug: string): boolean {
  if (!slug) return true
  const s = slug.toLowerCase()
  return (s.startsWith('assets/') ||
  s.startsWith('.well-known/') || /\.(woff2?|ttf|eot|ico|png|jpe?g|gif|webp|svg|js|css|json|xml|txt|map)$/i.test(slug));
}

export async function getStaticProps({
  preview,
  params,
  locale = 'de',
  locales,
}: GetStaticPropsContext<{ pages: string[] }>) {
  const slug = params?.pages?.join('/') ?? ''
  if (isAssetOrNonPagePath(slug)) {
    return { notFound: true }
  }
  const page = await getPageContent({ lang: locale, handle: slug }, preview)
  const global = await getGlobalContent({ lang: locale }, preview)
  if (!page) {
    console.error(`Page with slug '${slug}' not found`)
    return { notFound: true }
  }

  return {
    props: {
      page,
      global,
      messages: TRANSLATIONS[locale]
    },
    revalidate: 600
  }
}

export async function getStaticPaths({ locales }: GetStaticPathsContext) {
  const pagesQuery = await Promise.all(
    (locales as any)?.map((lang: string) => getAllPages({ lang }))
  )
  const pages = pagesQuery.flat()
  const paths = new Set(pages.map((page: any) => `/${page?.__i18n_lang}/${page?.handle?.current}`))
  return {
    paths: Array.from(paths),
    fallback: 'blocking',
  }
}

export default function Pages({
  page,
  global
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const router = useRouter()

  return router.isFallback ? (
    (<h1>Loading...</h1>) // TODO (BC) Add Skeleton Views
  ) : (
    <Page page={page} global={global} />
  );
}

Pages.Layout = Layout
